import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/lib/supabase-browser"
import { CLIENT_ID } from "@/lib/client-id"

// Query-key factories for the support chat (never inline the array — see CLAUDE.md).
export const supportMessagesKey = (conversationId: string | undefined) =>
  [`support`, `messages`, conversationId] as const
export const supportInboxKey = (status?: `open` | `closed`) =>
  [`support`, `inbox`, status ?? `all`] as const

// Subscribe to a support Realtime Broadcast topic and invalidate the given query
// keys when a `changed` signal arrives. Mirrors useTripBroadcast: the DB trigger
// (migration 0049) emits one nudge per support message with the originating
// client's id; we skip our own echo (our send already invalidates) so only the
// *other* party's message drives a refetch. The payload carries no row data, so
// the channel leaks nothing — the refetch goes through authorized tRPC.
export function useSupportBroadcast(
  topic: string | undefined,
  keysToInvalidate: readonly (readonly unknown[])[]
) {
  const queryClient = useQueryClient()
  const keySig = JSON.stringify(keysToInvalidate)

  useEffect(() => {
    if (!topic) return

    const onChanged = (message: { payload?: { client_id?: string } }) => {
      if (message.payload?.client_id === CLIENT_ID) return
      for (const queryKey of keysToInvalidate) {
        queryClient.invalidateQueries({ queryKey: [...queryKey] })
      }
    }

    const channel = supabase
      .channel(topic)
      .on(`broadcast`, { event: `changed` }, onChanged)
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
    // keysToInvalidate is compared by value via keySig (not by reference) to avoid
    // resubscribing on every render, since callers pass fresh array literals.
  }, [topic, keySig, queryClient, keysToInvalidate])
}
